import { Link, Tooltip } from "@heroui/react";
import { useLocation } from "react-router";

import type { NavItem } from "@/src/components/layout/navigation";
import { findNavItem, NAVIGATION } from "@/src/components/layout/navigation";
import { useTranslations } from "@/src/i18n/i18n";

type SidebarNavProps = {
  /** Rail réduit : les libellés disparaissent, les icônes restent. */
  isCollapsed?: boolean;
  /** Appelé après un clic — sert au tiroir mobile, qui doit se refermer. */
  onNavigate?: () => void;
};

/**
 * Les liens de l'administration.
 *
 * Le **même** composant sert la barre latérale de bureau et le tiroir mobile :
 * deux listes séparées finiraient par ne plus proposer les mêmes écrans.
 */
export function SidebarNav({
  isCollapsed = false,
  onNavigate,
}: SidebarNavProps) {
  const t = useTranslations("navigation");
  const ta = useTranslations("a11y");
  const { pathname } = useLocation();
  const activeKey = findNavItem(pathname)?.key;

  return (
    <nav
      aria-label={ta("main-navigation")}
      className={`flex flex-col ${isCollapsed ? "gap-5" : "gap-6"}`}
    >
      {NAVIGATION.map((group) => (
        <div className="flex flex-col gap-1" key={group.key}>
          {/*
            Réduit, l'intitulé de section n'est pas remplacé : c'est **le vide**
            qui regroupe. Quatre filets pour six icônes, c'était plus de
            séparateurs que de contenu, et un rail hachuré se lit moins vite
            qu'un rail aéré (20 px entre groupes contre 4 px à l'intérieur).
          */}
          {isCollapsed ? null : (
            <p className="px-3 pb-1 text-[11px] font-semibold tracking-wider text-muted uppercase">
              {t(group.key)}
            </p>
          )}

          <ul className={`flex flex-col ${isCollapsed ? "gap-1" : "gap-0.5"}`}>
            {group.items.map((item) => (
              <li key={item.key}>
                <NavLink
                  isActive={item.key === activeKey}
                  isCollapsed={isCollapsed}
                  item={item}
                  label={t(item.key)}
                  onNavigate={onNavigate}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

type NavLinkProps = {
  item: NavItem;
  label: string;
  isActive: boolean;
  isCollapsed: boolean;
  onNavigate?: () => void;
};

function NavLink({
  item,
  label,
  isActive,
  isCollapsed,
  onNavigate,
}: NavLinkProps) {
  const link = (
    <Link
      // `aria-current` est ce qui dit « vous êtes ici » aux lecteurs d'écran :
      // la couleur, elle, ne s'annonce pas.
      aria-current={isActive ? "page" : undefined}
      // Réduit, l'icône est seule : sans libellé accessible le lien s'annonce
      // par son URL.
      aria-label={isCollapsed ? label : undefined}
      className={[
        // `.link` de HeroUI est un lien *dans une phrase* : `w-fit`, et un
        // soulignement au survol. Dans une navigation les deux sont faux — la
        // pastille s'arrêterait au dernier caractère, donc « Messages » serait
        // plus large que « Reports ». Ses classes vivent dans `@layer
        // components`, ces utilitaires dans `@layer utilities` : ils gagnent.
        "flex w-full items-center gap-3 rounded-xl text-sm no-underline transition-colors hover:no-underline",
        // Réduit, l'item est un **carré de 40 px** (10 px + icône de 20 px) :
        // dans un rail de 64 px il occupe la largeur utile, donc la pastille
        // active est un carré et non une vignette perdue au milieu du vide.
        isCollapsed ? "justify-center px-0 py-2.5" : "px-3 py-2",
        // Les jetons `accent-soft` / `surface-hover` de HeroUI plutôt qu'une
        // opacité arbitraire : ils sont déjà dosés pour les deux thèmes (12 %
        // en sombre, 15 % en clair) et suivront un changement de couleur de
        // marque sans qu'on y revienne.
        isActive
          ? "bg-accent-soft font-medium text-accent-soft-foreground"
          : "text-muted hover:bg-surface-hover hover:text-foreground",
      ]
        .filter(Boolean)
        .join(" ")}
      href={item.path}
      onPress={onNavigate}
    >
      <item.Icon
        className={`shrink-0 ${isCollapsed ? "size-5" : "size-[18px]"}`}
      />
      {/* `min-w-0` : sans lui l'élément flex refuse de se comprimer sous sa
          largeur intrinsèque, et `truncate` ne tronque jamais. */}
      {isCollapsed ? null : <span className="min-w-0 truncate">{label}</span>}
    </Link>
  );

  if (!isCollapsed) return link;

  // Réduit, le libellé n'existe plus à l'écran : l'infobulle le rend au
  // survol et au clavier, sans quoi le rail devient un jeu de devinettes.
  return (
    <Tooltip>
      {link}
      <Tooltip.Content placement="right">{label}</Tooltip.Content>
    </Tooltip>
  );
}
